import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { DISTRICTS } from "../../shared/districts.ts";
import { formatDropTime, formatVnd } from "../../shared/i18n/index.ts";
import {
  DURATIONS,
  IMAGES_MAX,
  MAX_FLOOR_RATIO,
  MIN_START_PRICE,
  PRICE_STEP,
  QUANTITY_MAX,
} from "../../shared/limits.ts";
import { isDropWindow } from "../../shared/price.ts";
import { PriceDisplay } from "../components/PriceDisplay.tsx";
import { ApiError } from "../lib/api.ts";
import { serverNow, syncClock } from "../lib/clock.ts";
import { compressImage } from "../lib/compress.ts";
import { useI18n } from "../lib/i18n.tsx";
import {
  createDraft,
  dropInfo,
  loadOwner,
  publishDraft,
  removeImage,
  reorderImages,
  saveDraft,
  uploadImage,
  type ImageInfo,
  type OwnerListing,
} from "../lib/listing-form.ts";
import { useMe } from "../lib/me.tsx";
import { useMeta } from "../lib/meta.ts";

type Step = 0 | 1 | 2 | 3 | 4 | 5;

const steps = [
  "sell.step.photos",
  "sell.step.details",
  "sell.step.location",
  "sell.step.price",
  "sell.step.time",
  "sell.step.review",
] as const;

/** /sell → creates a draft and redirects to /sell/:id. /sell/:id is the six-step form (08-design.md §Sell). */
export function Sell() {
  const { id } = useParams();
  const { me, loading } = useMe();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || !me || !me.can_sell || id) return;
    createDraft()
      .then((r) => navigate(`/sell/${r.id}`, { replace: true }))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiError && e.body.error === "drafts_cap"
            ? t("sell.drafts_cap")
            : t("error.generic"),
        ),
      );
  }, [loading, me, id, navigate, t]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me)
    return (
      <Navigate to={`/login?next=${encodeURIComponent(id ? `/sell/${id}` : "/sell")}`} replace />
    );
  if (!me.can_sell) {
    return (
      <div className="page page--narrow">
        <h1 className="h1">{t("sell.title")}</h1>
        <p className="muted">{t("sell.need_invite")}</p>
        <a className="btn btn--accent" href="/me/settings">
          {t("settings.become_seller")}
        </a>
      </div>
    );
  }
  if (!id) return <div className="page muted">{error ?? t("common.loading")}</div>;
  return <SellForm id={id} />;
}

function SellForm({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const meta = useMeta();
  const [l, setL] = useState<OwnerListing | null>(null);
  const [step, setStep] = useState<Step>(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [drop, setDrop] = useState<{ for_listing: number; queued: number } | null>(null);
  const [uploading, setUploading] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    loadOwner(id)
      .then((o) => {
        if (!live) return;
        if (o.status !== "draft") {
          navigate(`/me/listings/${o.id}`, { replace: true });
          return;
        }
        setL(o);
      })
      .catch(() => live && setError(t("error.not_found")));
    dropInfo()
      .then((d) => {
        if (!live) return;
        syncClock(d.server_ts);
        setDrop(d);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [id, navigate, t]);

  function patch(p: Partial<OwnerListing>) {
    setL((cur) => (cur ? { ...cur, ...p } : cur));
    setError(null);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const body: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(p))
        if (k !== "images" && k !== "id" && k !== "status") body[k] = v;
      saveDraft(id, body).catch(() => setError(t("error.network")));
    }, 500);
  }

  async function onFiles(files: FileList | null) {
    if (!files || !l) return;
    const room = IMAGES_MAX - l.images.length;
    const list = Array.from(files).slice(0, Math.max(0, room));
    setUploading(list.length);
    for (const f of list) {
      try {
        const blob = await compressImage(f);
        const img = await uploadImage(id, blob);
        setL((cur) => (cur ? { ...cur, images: [...cur.images, img] } : cur));
      } catch {
        setError(t("error.generic"));
      } finally {
        setUploading((n) => n - 1);
      }
    }
    if (fileInput.current) fileInput.current.value = "";
  }

  async function move(img: ImageInfo, dir: -1 | 1) {
    if (!l) return;
    const ids = l.images.map((i) => i.id);
    const at = ids.indexOf(img.id);
    const to = at + dir;
    if (to < 0 || to >= ids.length) return;
    [ids[at], ids[to]] = [ids[to]!, ids[at]!];
    const images = ids.map((x, i) => ({ ...l.images.find((im) => im.id === x)!, sort_order: i }));
    setL({ ...l, images });
    await reorderImages(id, ids).catch(() => undefined);
  }

  async function remove(img: ImageInfo) {
    if (!l) return;
    setL({ ...l, images: l.images.filter((i) => i.id !== img.id) });
    await removeImage(id, img.id).catch(() => undefined);
  }

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        const { images: _i, id: _id, status: _s, ...rest } = l!;
        void _i;
        void _id;
        void _s;
        await saveDraft(id, {
          title: rest.title,
          description: rest.description ?? null,
          category_id: rest.category_id ?? null,
          condition: rest.condition ?? null,
          city_id: rest.city_id ?? null,
          district: rest.district ?? null,
          quantity: rest.quantity,
          start_price: rest.start_price ?? null,
          floor_price: rest.floor_price,
          duration_s: rest.duration_s ?? null,
          start_mode: rest.start_mode,
        });
      }
      const pub = await publishDraft(id);
      navigate(`/me/listings/${pub.id}?published=${pub.status}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        const m: Record<string, string> = {
          images_required: t("sell.photos.hint"),
          duration_unavailable: t("sell.duration.h6.drop_only"),
          listing_cap: t("sell.cap_reached"),
          validation: `${t("error.validation")} ${(e.body.issues ?? []).map((i) => i.path).join(", ")}`,
        };
        setError(m[e.body.error] ?? t("error.generic"));
      } else setError(t("error.network"));
    } finally {
      setBusy(false);
    }
  }

  if (!l) return <div className="page muted">{error ?? t("common.loading")}</div>;

  const now = serverNow();
  const leaves = meta?.categories.filter((c) => c.parent_id !== null) ?? [];
  const citySlug = meta?.cities.find((c) => c.id === l.city_id)?.slug;
  const districts = citySlug ? (DISTRICTS[citySlug] ?? []) : [];
  const startAt = l.start_mode === "drop" ? (drop?.for_listing ?? null) : now;
  const sixAllowed = startAt !== null && isDropWindow(startAt);
  const floorMax = l.start_price
    ? Math.floor((l.start_price * MAX_FLOOR_RATIO) / PRICE_STEP) * PRICE_STEP
    : 0;
  const canNext = [
    l.images.length >= 1,
    (l.title ?? "").trim().length >= 5 && !!l.category_id && !!l.condition,
    !!l.city_id,
    (l.start_price ?? 0) >= MIN_START_PRICE && l.floor_price <= floorMax,
    !!l.duration_s && (l.duration_s !== DURATIONS.h6 || sixAllowed),
    true,
  ][step];

  const seg = l.start_price
    ? {
        price: l.start_price,
        rate_per_s: 0,
        server_ts: now,
        bucket: "days" as const,
        status: "active" as const,
      }
    : null;

  return (
    <div className="page page--narrow">
      <ol className="steps" aria-label={t("sell.title")}>
        {steps.map((k, i) => (
          <li
            key={k}
            className={`steps__item${i === step ? " is-active" : i < step ? " is-done" : ""}`}
          >
            <button type="button" onClick={() => i < step && setStep(i as Step)}>
              {t(k)}
            </button>
          </li>
        ))}
      </ol>
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {step === 0 ? (
        <section className="form">
          <h1 className="h1">{t("sell.step.photos")}</h1>
          <p className="muted small">{t("sell.photos.hint")}</p>
          <div className="thumbs">
            {l.images.map((img, i) => (
              <figure key={img.id} className="thumb">
                <img src={img.url} alt="" width={img.width} height={img.height} />
                {i === 0 ? <figcaption className="thumb__cover">1</figcaption> : null}
                <div className="thumb__actions">
                  <button
                    type="button"
                    className="iconbtn"
                    aria-label="←"
                    onClick={() => void move(img, -1)}
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    className="iconbtn"
                    aria-label="→"
                    onClick={() => void move(img, 1)}
                  >
                    →
                  </button>
                  <button
                    type="button"
                    className="iconbtn"
                    aria-label={t("common.cancel")}
                    onClick={() => void remove(img)}
                  >
                    ✕
                  </button>
                </div>
              </figure>
            ))}
            {l.images.length < IMAGES_MAX ? (
              <label className="thumb thumb--add">
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => void onFiles(e.target.files)}
                  data-testid="photo-input"
                />
                <span>{uploading > 0 ? `… ${uploading}` : "+"}</span>
              </label>
            ) : null}
          </div>
        </section>
      ) : null}

      {step === 1 ? (
        <section className="form">
          <h1 className="h1">{t("sell.step.details")}</h1>
          <label className="field">
            <span className="label">{t("sell.field.title")}</span>
            <input
              className="input"
              value={l.title ?? ""}
              onChange={(e) => patch({ title: e.target.value.slice(0, 80) })}
              minLength={5}
              maxLength={80}
              data-testid="sell-title"
            />
          </label>
          <label className="field">
            <span className="label">{t("sell.field.category")}</span>
            <select
              className="input"
              value={l.category_id ?? ""}
              onChange={(e) =>
                patch({ category_id: e.target.value ? Number(e.target.value) : null })
              }
              data-testid="sell-category"
            >
              <option value="">—</option>
              {leaves.map((c) => (
                <option key={c.id} value={c.id}>
                  {locale === "vi" ? c.name_vi : c.name_en}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="field">
            <legend className="label">{t("sell.field.condition")}</legend>
            <div className="chips">
              {(["new", "like_new", "good", "fair"] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`chip${l.condition === c ? " is-on" : ""}`}
                  onClick={() => patch({ condition: c })}
                  data-testid={`cond-${c}`}
                >
                  {t(`listing.condition.${c}`)}
                </button>
              ))}
            </div>
          </fieldset>
          <label className="field">
            <span className="label">{t("sell.field.description")}</span>
            <textarea
              className="input"
              rows={5}
              value={l.description ?? ""}
              onChange={(e) => patch({ description: e.target.value.slice(0, 2000) })}
              maxLength={2000}
            />
          </label>
          <label className="field">
            <span className="label">{t("sell.field.quantity")}</span>
            <input
              className="input"
              type="number"
              min={1}
              max={QUANTITY_MAX}
              value={l.quantity}
              onChange={(e) =>
                patch({
                  quantity: Math.min(QUANTITY_MAX, Math.max(1, Number(e.target.value) || 1)),
                })
              }
            />
          </label>
        </section>
      ) : null}

      {step === 2 ? (
        <section className="form">
          <h1 className="h1">{t("sell.step.location")}</h1>
          <label className="field">
            <span className="label">{t("sell.field.city")}</span>
            <select
              className="input"
              value={l.city_id ?? ""}
              onChange={(e) =>
                patch({ city_id: e.target.value ? Number(e.target.value) : null, district: null })
              }
            >
              {meta?.cities
                .filter((c) => c.enabled)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {locale === "vi" ? c.name_vi : c.name_en}
                  </option>
                ))}
            </select>
          </label>
          <label className="field">
            <span className="label">{t("sell.field.district")}</span>
            <input
              className="input"
              list="sell-districts"
              value={l.district ?? ""}
              onChange={(e) => patch({ district: e.target.value || null })}
              maxLength={60}
            />
            <datalist id="sell-districts">
              {districts.map((d) => (
                <option key={d} value={d} />
              ))}
            </datalist>
          </label>
        </section>
      ) : null}

      {step === 3 ? (
        <section className="form">
          <h1 className="h1">{t("sell.step.price")}</h1>
          <label className="field">
            <span className="label">{t("sell.field.start_price")}</span>
            <input
              className="input"
              type="number"
              inputMode="numeric"
              min={MIN_START_PRICE}
              step={PRICE_STEP}
              value={l.start_price ?? ""}
              onChange={(e) =>
                patch({
                  start_price: e.target.value
                    ? Math.round(Number(e.target.value) / PRICE_STEP) * PRICE_STEP
                    : null,
                })
              }
              data-testid="sell-price"
            />
            {l.start_price ? <span className="hint">{formatVnd(l.start_price)}</span> : null}
          </label>
          <details className="field" open={l.floor_price > 0}>
            <summary className="label">{t("sell.field.floor_price")}</summary>
            <input
              className="input"
              type="number"
              inputMode="numeric"
              min={0}
              max={floorMax}
              step={PRICE_STEP}
              value={l.floor_price}
              onChange={(e) =>
                patch({
                  floor_price: Math.min(
                    floorMax,
                    Math.max(0, Math.round(Number(e.target.value) / PRICE_STEP) * PRICE_STEP),
                  ),
                })
              }
            />
            <span className="hint">
              {l.floor_price > 0
                ? t("price.floor.set", { price: formatVnd(l.floor_price) })
                : t("price.floor.none")}
            </span>
          </details>
        </section>
      ) : null}

      {step === 4 ? (
        <section className="form">
          <h1 className="h1">{t("sell.step.time")}</h1>
          <fieldset className="field">
            <legend className="label">{t("sell.field.duration")}</legend>
            <div className="chips">
              {(["h6", "h24", "d3", "d7"] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`chip${l.duration_s === DURATIONS[k] ? " is-on" : ""}`}
                  disabled={k === "h6" && !sixAllowed}
                  onClick={() => patch({ duration_s: DURATIONS[k] })}
                  data-testid={`dur-${k}`}
                >
                  {t(
                    k === "h6" && !sixAllowed ? "sell.duration.h6.drop_only" : `sell.duration.${k}`,
                  )}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset className="field">
            <legend className="label">{t("sell.step.time")}</legend>
            <div className="stack">
              <label className="check">
                <input
                  type="radio"
                  name="start"
                  checked={l.start_mode === "drop"}
                  onChange={() => patch({ start_mode: "drop" })}
                  data-testid="start-drop"
                />
                <span>
                  {t("sell.start.drop", {
                    when: drop
                      ? formatDropTime(drop.for_listing, locale, now)
                      : t("drop.time_label"),
                  })}
                  {drop ? (
                    <span className="hint">
                      {" "}
                      · {t("sell.start.drop.queued", { n: drop.queued })}
                    </span>
                  ) : null}
                </span>
              </label>
              <label className="check">
                <input
                  type="radio"
                  name="start"
                  checked={l.start_mode === "now"}
                  onChange={() => patch({ start_mode: "now" })}
                  data-testid="start-now"
                />
                <span>{t("sell.start.now")}</span>
              </label>
            </div>
          </fieldset>
        </section>
      ) : null}

      {step === 5 ? (
        <section className="form">
          <h1 className="h1">{t("sell.step.review")}</h1>
          <p className="muted small">{t("sell.preview")}</p>
          <article className="card">
            {l.images[0] ? (
              <img className="card__cover" src={l.images[0].url} alt="" />
            ) : (
              <div className="card__cover" />
            )}
            <div className="card__body">
              <h3 className="card__title">{l.title}</h3>
              <p className="card__meta">
                {l.condition ? t(`listing.condition.${l.condition}`) : ""}
                {l.district ? ` · ${l.district}` : ""}
              </p>
              {seg ? <PriceDisplay segment={seg} size="sm" /> : null}
              <p className="muted small">
                {l.start_mode === "drop"
                  ? t("sell.start.drop", {
                      when: drop ? formatDropTime(drop.for_listing, locale, now) : "",
                    })
                  : t("sell.start.now")}{" "}
                ·{" "}
                {l.duration_s === DURATIONS.h6
                  ? t("sell.duration.h6")
                  : l.duration_s === DURATIONS.h24
                    ? t("sell.duration.h24")
                    : l.duration_s === DURATIONS.d3
                      ? t("sell.duration.d3")
                      : t("sell.duration.d7")}
              </p>
            </div>
          </article>
          <p className="muted small">{t("price.rule")}</p>
          <button
            className="btn btn--accent btn--block"
            disabled={busy}
            onClick={() => void publish()}
            data-testid="sell-publish"
          >
            {t("sell.publish")}
          </button>
        </section>
      ) : null}

      <div className="row row--between">
        <button
          className="btn btn--ghost"
          type="button"
          disabled={step === 0}
          onClick={() => setStep((s) => (s - 1) as Step)}
        >
          {t("common.back")}
        </button>
        {step < 5 ? (
          <button
            className="btn btn--accent"
            type="button"
            disabled={!canNext}
            onClick={() => setStep((s) => (s + 1) as Step)}
            data-testid="sell-next"
          >
            {t("common.next")}
          </button>
        ) : null}
      </div>
    </div>
  );
}
